import api from '../api/client';
// components/ConsumptionHistoryModal.jsx
import React, { useState, useEffect } from 'react';
import { Modal, Table, Select, DatePicker, Button, Space, Row, Col, Statistic, Card, message, Tooltip } from 'antd';
import { DownloadOutlined, FilterOutlined, ClearOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import config from '../config';
import { apiFetch } from '../api';

const { RangePicker } = DatePicker;
const { Option } = Select;

const environment = window.location.hostname === 'localhost' ? 'development' : 'production';
const API = config[environment].apiUrl;

// const apiFetch = async (path, opts = {}) => {
//     const r = await fetch(`${API}${path}`, { headers: { 'Content-Type': 'application/json' }, ...opts });
//     const d = await r.json();
//     if (!r.ok) throw new Error(d.message || 'Request failed');
//     return d;
// };

const ConsumptionHistoryModal = ({ assetId, assetName, open, onClose }) => {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [summary, setSummary] = useState(null);
    const [filters, setFilters] = useState({
        itemCategory: undefined,
        dateRange: null
    });

    const loadData = async () => {
        if (!assetId) return;
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (filters.itemCategory) params.append('itemCategory', filters.itemCategory);
            if (filters.dateRange && filters.dateRange[0]) params.append('startDate', filters.dateRange[0].format('YYYY-MM-DD'));
            if (filters.dateRange && filters.dateRange[1]) params.append('endDate', filters.dateRange[1].format('YYYY-MM-DD'));
            const res = await apiFetch(`/api/animal-assets/${assetId}/consumptions?${params.toString()}`);
            setData(res.consumptions);
            setSummary(res.summary);
        } catch (err) {
            message.error(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open && assetId) loadData();
    }, [open, assetId, filters]);

    const exportCSV = async () => {
        const params = new URLSearchParams({
            export: 'true',
            ...(filters.itemCategory && { itemCategory: filters.itemCategory }),
            ...(filters.dateRange && filters.dateRange[0] && { startDate: filters.dateRange[0].format('YYYY-MM-DD') }),
            ...(filters.dateRange && filters.dateRange[1] && { endDate: filters.dateRange[1].format('YYYY-MM-DD') })
        });
        window.open(`/api/animal-assets/${assetId}/consumptions?${params.toString()}`, '_blank');
    };

    const columns = [
        {
            title: 'Date',
            dataIndex: 'date',
            render: (v) => dayjs(v).format('DD MMM YYYY'),
            sorter: (a, b) => dayjs(a.date).unix() - dayjs(b.date).unix()
        },
        { title: 'Item', dataIndex: 'itemName' },
        { title: 'Category', dataIndex: 'itemCategory' },
        { title: 'Total Qty', dataIndex: 'quantity', align: 'right', render: (v) => v?.toFixed(2) },
        { title: 'Animals', dataIndex: 'animalCount', align: 'right' },
        { title: 'Per Animal Qty', dataIndex: 'perAnimalQuantity', align: 'right', render: (v) => v?.toFixed(2) },
        { title: 'Unit Cost', dataIndex: 'unit_cost', align: 'right', render: (v) => `KES ${v?.toFixed(2)}` },
        { title: 'Per Animal Cost', dataIndex: 'perAnimalCost', align: 'right', render: (v) => `KES ${v?.toFixed(2)}` },
        { title: 'Remarks', dataIndex: 'remarks', ellipsis: true }
    ];

    return (
        <Modal
            title={`Consumption History – ${assetName || assetId}`}
            open={open}
            onCancel={onClose}
            width={1200}
            footer={null}
            destroyOnClose
        >
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                {/* Filters */}
                <Row gutter={16} align="bottom">
                    <Col span={6}>
                        <Select
                            placeholder="Item Category"
                            allowClear
                            value={filters.itemCategory}
                            onChange={(v) => setFilters({ ...filters, itemCategory: v })}
                            style={{ width: '100%' }}
                        >
                            <Option value="feed">Feed</Option>
                            <Option value="vaccine">Vaccine</Option>
                            <Option value="supplement">Supplement</Option>
                            <Option value="medicine">Medicine</Option>
                        </Select>
                    </Col>
                    <Col span={10}>
                        <RangePicker
                            style={{ width: '100%' }}
                            value={filters.dateRange}
                            onChange={(dates) => setFilters({ ...filters, dateRange: dates })}
                        />
                    </Col>
                    <Col span={8}>
                        <Space>
                            <Button icon={<FilterOutlined />} onClick={loadData}>
                                Apply
                            </Button>
                            <Button icon={<ClearOutlined />} onClick={() => setFilters({ itemCategory: undefined, dateRange: null })}>
                                Clear
                            </Button>
                            <Button icon={<DownloadOutlined />} onClick={exportCSV}>
                                Export CSV
                            </Button>
                        </Space>
                    </Col>
                </Row>

                {/* Summary Cards */}
                {summary && (
                    <Row gutter={16}>
                        <Col span={6}>
                            <Card size="small">
                                <Statistic
                                    title="Total Cost (Per Animal)"
                                    value={summary.totalConsumptionCost}
                                    precision={2}
                                    prefix="KES"
                                />
                            </Card>
                        </Col>
                        <Col span={6}>
                            <Card size="small">
                                <Statistic title="Total Quantity (Per Animal)" value={summary.totalQuantity} precision={2} />
                            </Card>
                        </Col>
                        {Object.entries(summary.byCategory).map(([cat, vals]) => (
                            <Col span={6} key={cat}>
                                <Card size="small">
                                    <Statistic title={`${cat} Cost`} value={vals.cost} precision={2} prefix="KES" />
                                    <div style={{ fontSize: 12 }}>Qty: {vals.quantity.toFixed(2)}</div>
                                </Card>
                            </Col>
                        ))}
                    </Row>
                )}

                {/* Table */}
                <Table
                    columns={columns}
                    dataSource={data}
                    rowKey="id"
                    loading={loading}
                    pagination={{ pageSize: 15, showSizeChanger: true }}
                    scroll={{ x: 1000 }}
                />
            </Space>
        </Modal>
    );
};

export default ConsumptionHistoryModal;
